import React, { ReactChild, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { ImperativePanelHandle, Panel } from 'react-resizable-panels';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import CloseSection from '../close-section/close-section';
import useForwardRef from '../hooks/use-forward-ref';

import styles from './panel-content.module.scss';

interface PanelContentProps {
	children: ReactChild;
	collapsible?: boolean;
	sectionName?: string;
	minSize?: number;
	maxSize?: number;
	collapsed?: boolean;
	defaultSize?: number;
	unmountOnCollapse?: boolean;
	onPanelCollapse?: (collapse: boolean) => void;
}

const PanelContent = React.forwardRef<ImperativePanelHandle, PanelContentProps>((props, ref) => {
	const {
		children,
		collapsed = false,
		collapsible = true,
		sectionName = '',
		minSize = 1,
		maxSize = 100,
		defaultSize,
		onPanelCollapse,
		unmountOnCollapse = false
	} = props;
	const panelRef = useForwardRef(ref);
	const [isCollapsed, setIsCollapsed] = useState<boolean>(collapsed);
	const { t } = useTranslation();

	const renderChildren = unmountOnCollapse ? !isCollapsed : true;

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		if (collapsed && !panelRef.current?.getCollapsed()) {
			timeoutId = setTimeout(() => {
				panelRef.current?.collapse();
			}, 0);
		}

		return () => {
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [panelRef, collapsed]);

	return (
		<>
			<Panel
				collapsible={collapsible}
				minSize={minSize}
				maxSize={maxSize}
				ref={panelRef}
				onCollapse={setIsCollapsed}
				className={styles.panelContainer}
				defaultSize={defaultSize}
			>
				{renderChildren && children}
				{collapsible && (
					<Tooltip title={`${t('ROUTE_MONITORING.TOOLTIP.MINIMIZE')} ${sectionName}`}>
						<Button
							onClick={() => onPanelCollapse?.(true)}
							className={styles.closeSection}
							type="ghost"
							data-test-id={`close-button-${sectionName}`}
						>
							<BringgIcon iconName={BringgFontIcons.ChevronRight} className={styles.firstIcon} />
							<BringgIcon iconName={BringgFontIcons.ChevronLeft} />
						</Button>
					</Tooltip>
				)}
			</Panel>
			{isCollapsed && (
				<CloseSection
					sectionName={sectionName}
					openSection={() => {
						onPanelCollapse?.(false);
					}}
				/>
			)}
		</>
	);
});

export default observer(PanelContent);
